import {
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  UniqueIdentifier,
} from "@dnd-kit/core"
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { Column, Table } from "@tanstack/react-table"
import { useCallback, useMemo, useState } from "react"

import { CHECKBOX_COLUMN_ID } from "../../organisms/Table/columnHelpers"

import { getColumnLabel } from "./utils"

export type FilteredColumns<T> = {
  filteredPinnedColumns: Column<T, unknown>[]
  filteredVisibleColumns: Column<T, unknown>[]
  filteredHiddenColumns: Column<T, unknown>[]
}

const isDroppedInVisibleArea = <T>(
  overId: UniqueIdentifier,
  visibleColumns: Column<T>[]
): boolean => {
  return overId === "visible" || visibleColumns.some((col) => col.id === overId)
}

const isDroppedInHiddenArea = <T>(
  overId: UniqueIdentifier,
  hiddenColumns: Column<T>[]
): boolean => {
  return overId === "hidden" || hiddenColumns.some((col) => col.id === overId)
}

const shouldToggleVisibility = (
  isActiveVisible: boolean,
  isDroppedInVisible: boolean,
  isDroppedInHidden: boolean
): boolean => {
  return (
    (isActiveVisible && isDroppedInHidden) ||
    (!isActiveVisible && isDroppedInVisible)
  )
}

const isReorderOperation = (
  activeId: UniqueIdentifier,
  overId: UniqueIdentifier
): boolean => {
  return activeId !== overId && overId !== "visible" && overId !== "hidden"
}

const reorderColumns = <T>(
  table: Table<T>,
  activeId: UniqueIdentifier,
  overId: UniqueIdentifier
): void => {
  const fullColumnOrder = table.getState().columnOrder
  const oldIndex = fullColumnOrder.findIndex((id) => id === activeId)
  const newIndex = fullColumnOrder.findIndex((id) => id === overId)

  if (oldIndex === -1 || newIndex === -1) {
    return
  }

  const newColumnOrder = [...fullColumnOrder]
  const [movedItem] = newColumnOrder.splice(oldIndex, 1)
  newColumnOrder.splice(newIndex, 0, movedItem)

  table.setColumnOrder(newColumnOrder)
}

export const useGetColumnMap = <T>(table: Table<T>) => {
  return useMemo(
    () => new Map(table.getAllColumns().map((col) => [col.id, col])),
    [table.getAllColumns()]
  )
}

export const useInitDndContext = <T>(table: Table<T>) => {
  const columnMap = useGetColumnMap(table)
  const { filteredVisibleColumns, filteredHiddenColumns } =
    useGetFilteredColumns(table)
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const draggedId = event.active.id
    setActiveId(draggedId)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      setActiveId(null)

      if (!over) return

      const activeId = active.id
      const overId = over.id
      const activeColumn = columnMap.get(String(activeId))

      if (!activeColumn || activeColumn.getIsPinned()) return

      const isActiveVisible = activeColumn.getIsVisible()
      const isDroppedInVisible = isDroppedInVisibleArea(
        overId,
        filteredVisibleColumns
      )
      const isDroppedInHidden = isDroppedInHiddenArea(
        overId,
        filteredHiddenColumns
      )

      if (
        shouldToggleVisibility(
          isActiveVisible,
          isDroppedInVisible,
          isDroppedInHidden
        )
      ) {
        activeColumn.toggleVisibility(!isActiveVisible)
      }

      if (isReorderOperation(activeId, overId)) {
        reorderColumns(table, activeId, overId)
      }
    },
    [columnMap, filteredVisibleColumns, filteredHiddenColumns, table]
  )

  return { sensors, activeId, setActiveId, handleDragStart, handleDragEnd }
}

export const useGetActiveColumn = <T>(
  columnMap: Map<UniqueIdentifier, Column<T>>,
  activeId: UniqueIdentifier | null
) => {
  return useMemo(() => {
    return activeId ? columnMap.get(activeId) : null
  }, [columnMap, activeId])
}

export const useGetOrderedColumns = <T>(table: Table<T>) => {
  const columnOrder = table.getState().columnOrder
  const columnMap = useGetColumnMap(table)
  return useMemo(
    () =>
      columnOrder
        .map((id) => columnMap.get(id))
        .filter(
          (col): col is NonNullable<typeof col> =>
            col !== undefined &&
            col.id !== CHECKBOX_COLUMN_ID &&
            !col.id.startsWith("action-cell")
        ),
    [columnOrder, columnMap]
  )
}

export const useGetFilteredColumns = <T>(
  table: Table<T>,
  searchQuery?: string
): FilteredColumns<T> => {
  const columnVisibility = table.getState().columnVisibility
  const pinnedColumnState = table.getState().columnPinning?.left ?? []
  const orderedColumns = useGetOrderedColumns(table)
  const { pinnedColumns, visibleColumns, hiddenColumns } = useMemo(() => {
    const pinned = orderedColumns.filter((col) =>
      pinnedColumnState.includes(col.id)
    )
    const unpinnedColumns = orderedColumns.filter(
      (col) => !pinnedColumnState.includes(col.id)
    )
    const visible = unpinnedColumns.filter(
      (col) => columnVisibility[col.id] !== false
    )
    const hidden = unpinnedColumns.filter(
      (col) => columnVisibility[col.id] === false
    )
    return {
      pinnedColumns: pinned,
      visibleColumns: visible,
      hiddenColumns: hidden,
    }
  }, [orderedColumns, columnVisibility, pinnedColumnState])

  const toFilterColumns = useCallback(
    (columns: Column<T>[]) => {
      if (!searchQuery?.trim()) return columns
      const query = searchQuery.toLowerCase()
      return columns.filter((column) => {
        const label = getColumnLabel(column).toLowerCase()
        const id = column.id.toLowerCase()
        return label.includes(query) || id.includes(query)
      })
    },
    [searchQuery]
  )
  const filteredPinnedColumns = useMemo(
    () => toFilterColumns(pinnedColumns),
    [pinnedColumns, toFilterColumns]
  )

  const filteredVisibleColumns = useMemo(
    () => toFilterColumns(visibleColumns),
    [visibleColumns, toFilterColumns]
  )

  const filteredHiddenColumns = useMemo(
    () => toFilterColumns(hiddenColumns),
    [hiddenColumns, toFilterColumns]
  )

  return {
    filteredPinnedColumns,
    filteredVisibleColumns,
    filteredHiddenColumns,
  }
}
